import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const productCategories = [
    'Office Chairs',
    'Tables',
    'Workstations',
    'Storage Units',
    'Conference Tables',
    'Lounge Seating'
  ];

  const address = 'AARTEMA, 1st Floor, C-26C, Main Road, Block F, Mansarover Garden, New Delhi, Delhi, 110015';
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  const phone = '+919350056070'; // international format
  const mail = 'vikas.mangla@outlook.com';
  const mailtoUrl = `mailto:${mail}?subject=${encodeURIComponent('Enquiry from website')}`;

  return (
    <footer className="bg-[#222] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-[#C1A57B]">AARTEMA</h3>
            <p className="text-gray-300 mb-4 text-sm leading-relaxed">
              Redefining workspaces with premium office furniture solutions. Quality craftsmanship meets modern design.
            </p>
            <div className="flex space-x-3">
              <a
                href="https://www.linkedin.com" /* replace with actual URL */
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#C1A57B]/10 flex items-center justify-center hover:bg-[#C1A57B] transition-colors duration-300 group"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} className="text-[#C1A57B] group-hover:text-white" />
              </a>
              <a
                href="https://www.facebook.com" /* replace with actual URL */
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#C1A57B]/10 flex items-center justify-center hover:bg-[#C1A57B] transition-colors duration-300 group"
                aria-label="Facebook"
              >
                <Facebook size={18} className="text-[#C1A57B] group-hover:text-white" />
              </a>
              <a
                href="https://www.instagram.com" /* replace with actual URL */
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#C1A57B]/10 flex items-center justify-center hover:bg-[#C1A57B] transition-colors duration-300 group"
                aria-label="Instagram"
              >
                <Instagram size={18} className="text-[#C1A57B] group-hover:text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#C1A57B]">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-[#C1A57B] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#C1A57B]">Products</h4>
            <ul className="space-y-2">
              {productCategories.map((category) => (
                <li key={category}>
                  <Link
                    to="/products"
                    className="text-gray-300 hover:text-[#C1A57B] transition-colors duration-200 text-sm"
                  >
                    {category}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info (clickable) */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#C1A57B]">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-3 text-gray-300 text-sm hover:text-[#C1A57B] transition-colors duration-150"
                  aria-label="Open address in Google Maps"
                >
                  <MapPin size={18} className="text-[#C1A57B] mt-1 flex-shrink-0" />
                  <span>
                    <strong className="block">Showroom Address</strong>
                    <span className="block">{address}</span>
                  </span>
                </a>
              </li>

              <li>
                <a
                  href={`tel:${phone}`}
                  className="flex items-center space-x-3 text-gray-300 text-sm hover:text-[#C1A57B] transition-colors duration-150"
                  aria-label={`Call ${phone}`}
                >
                  <Phone size={18} className="text-[#C1A57B] flex-shrink-0" />
                  <span>{phone}</span>
                </a>
              </li>

              <li>
                <a
                  href={mailtoUrl}
                  className="flex items-center space-x-3 text-gray-300 text-sm hover:text-[#C1A57B] transition-colors duration-150"
                  aria-label={`Email ${mail}`}
                >
                  <Mail size={18} className="text-[#C1A57B] flex-shrink-0" />
                  <span>{mail}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} AARTEMA Office Furniture. All Rights Reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-[#C1A57B] transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-[#C1A57B] transition-colors duration-200">
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
